import { type Component } from 'solid-js';
import { formatCurrency, formatPercent, perYear } from '~/lib/format';
import { Card } from '~/features/cards';

// The borrowed side of the book, with the answer first.
//
// Profit leads because it is the only one of these that is a verdict: the rest
// are inputs to it, and reading them first means working out for yourself what
// they come to. Then its two halves in the order they subtract — what the
// borrowed money brings in, then what it costs — followed by the balance that
// cost is charged on and the share of it that comes back.
//
// Figures are given rather than fetched, on the same terms as the Accounts row:
// the real book and the Sandbox both show this and compute it from different
// places, and passing it in is what keeps that a difference of source rather
// than two hand-kept copies that drift.

export interface LoanFigures {
    /** Dollars a month the borrowing nets, after its own interest. */
    carryPerMonth: number | null;
    /** Yield less borrow rate, in points. */
    spread: number | null;
    yieldPct: number | null;
    interestPerYear: number;
    borrowRate: number | null;
    debt: number;
    /** Debt as a share of market value. */
    leverage: number | null;
    /** The share of interest that is deductible, per year. */
    deductiblePerYear: number;
    /** What the positions the borrowing paid for pay, per year — before the
        interest is taken off it. Null while any of them is unpriced. */
    dividendsPerYear: number | null;
}

const LoanCards: Component<{ figures: LoanFigures }> = (props) => (
    <div class='flex gap-px border-b bg-border'>
        <div class='min-w-0 flex-1'>
            {/* What the leverage nets after the cost of it. Dollars rather than
                the rate gap, because the gap is not the thing you can spend. N/A
                with no loans — there is no spread without a borrowing side, and
                zero would claim one. */}
            <Card
                label='Profit from loans'
                value={formatCurrency(props.figures.carryPerMonth)}
                tone={props.figures.carryPerMonth == null ? undefined : props.figures.carryPerMonth >= 0 ? 'green' : 'red'}>
                {formatPercent(props.figures.spread, 2)} spread · {formatPercent(props.figures.yieldPct, 2)} yield
            </Card>
        </div>
        <div class='min-w-0 flex-1'>
            {/* The gross side of the card to its left, before the interest is
                taken off it. Worth its own card rather than a line on that one:
                a thin profit made of a large dividend and a large interest bill
                is a different position from a thin profit made of two small
                ones, and the net figure alone cannot tell you which you have. */}
            <Card
                label='Dividends from loans'
                value={formatCurrency(props.figures.dividendsPerYear == null ? null : props.figures.dividendsPerYear / 12)}
                tone={props.figures.dividendsPerYear == null || props.figures.dividendsPerYear <= 0 ? undefined : 'green'}>
                {perYear(props.figures.dividendsPerYear)} · from what the loans bought
            </Card>
        </div>
        <div class='min-w-0 flex-1'>
            <Card label='Interest per month' value={formatCurrency(props.figures.interestPerYear / 12)} tone={props.figures.interestPerYear > 0 ? 'red' : undefined}>
                {perYear(props.figures.interestPerYear)} · {formatPercent(props.figures.borrowRate, 2)} borrowed
            </Card>
        </div>
        <div class='min-w-0 flex-1'>
            <Card label='Debt' value={formatCurrency(props.figures.debt)} tone={props.figures.debt > 0 ? 'red' : undefined}>
                {formatPercent(props.figures.leverage, 1)} of book
            </Card>
        </div>
        <div class='min-w-0 flex-1'>
            {/* Which share qualifies is not a choice — it falls out of the account
                the borrowed money was invested in, and sheltering the income
                sheltered the deduction with it. */}
            <Card label='Deductible per month' value={formatCurrency(props.figures.deductiblePerYear / 12)}>
                {perYear(props.figures.deductiblePerYear)} · taxable-account share only
            </Card>
        </div>
    </div>
);

export default LoanCards;
